import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/* ─── inject styles once ─── */
const RESULTS_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  .r-card { transition: box-shadow 0.2s, border-color 0.2s; }
  .r-card:hover { border-color: #93c5fd !important; box-shadow: 0 4px 20px rgba(59,130,246,0.08) !important; }

  .r-pubmed-btn:hover { background: #dbeafe !important; }
  .r-back-btn:hover { background: #dbeafe !important; }

  .r-tab-active {
    background: linear-gradient(135deg,#1e3a8a,#3b82f6) !important;
    color: #fff !important;
    box-shadow: 0 2px 8px rgba(59,130,246,0.25) !important;
  }

  @keyframes r-fadein {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .r-cards-list { animation: r-fadein 0.25s ease; }

  @media (max-width: 768px) {
    .r-layout   { flex-direction: column !important; }
    .r-sidebar  { width: 100% !important; flex-direction: row !important; flex-wrap: wrap; gap: 12px !important; }
    .r-panel    { flex: 1; min-width: 240px; }
    .r-stat-grid { grid-template-columns: repeat(4, 1fr) !important; }
    .r-tabs-wrap { overflow-x: auto; }
  }
  @media (max-width: 480px) {
    .r-layout { padding: 16px !important; }
    .r-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }
`;

function injectStyles() {
  if (typeof document !== "undefined" && !document.getElementById("r-styles")) {
    const s = document.createElement("style");
    s.id = "r-styles";
    s.textContent = RESULTS_CSS;
    document.head.appendChild(s);
  }
}

/* ─── sub-components ─── */
function BackArrow() {
  return (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="#1e3a8a"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg
      width="12"
      height="12"
      fill="none"
      stroke="#1e3a8a"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke="#94a3b8"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function PaperCard({ paper, index }) {
  return (
    <div
      className="r-card"
      style={{
        background: "#fff",
        border: "1px solid #e2e8f8",
        borderRadius: 14,
        padding: "18px 20px",
        marginBottom: 12,
      }}
    >
      {/* top row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        {/* index badge */}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: "#eff6ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 800,
            color: "#1e3a8a",
            flexShrink: 0,
            fontFamily: "'DM Mono', monospace",
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </div>

        {/* body */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.45,
              marginBottom: 8,
            }}
          >
            {paper.title}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                background: "#f1f5f9",
                color: "#475569",
                borderRadius: 100,
                padding: "3px 10px",
              }}
            >
              PMID {paper.pmid}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                background: "#eff6ff",
                color: "#1e3a8a",
                borderRadius: 100,
                padding: "3px 10px",
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {paper.year}
            </span>
          </div>
        </div>
      </div>

      {/* footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 12,
          paddingTop: 12,
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <a
          href={paper.link}
          target="_blank"
          rel="noreferrer"
          className="r-pubmed-btn"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 700,
            color: "#1e3a8a",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 8,
            padding: "6px 12px",
            textDecoration: "none",
            transition: "background 0.15s",
          }}
        >
          View on PubMed
          <ExternalIcon />
        </a>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 220,
        gap: 10,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EmptyIcon />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#94a3b8" }}>
        No {label} available
      </div>
    </div>
  );
}

/* ─── main component ─── */
export default function ArticleMinerResults() {
  injectStyles();

  const { state } = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  /* empty / error state */
  if (!state?.result) {
    return (
      <div
        style={{
          textAlign: "center",
          marginTop: 80,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ fontSize: 15, color: "#64748b", marginBottom: 16 }}>
          No results found
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            background: "linear-gradient(135deg,#1e3a8a,#3b82f6)",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "10px 20px",
            cursor: "pointer",
          }}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  const { query, yearRange, mode, result } = state;

  /* tab config by mode */
  const tabs =
    mode === "A"
      ? [
          { label: "Research Articles", data: result.research },
          { label: "Review Articles", data: result.review },
        ]
      : [
          { label: "Highly Cited", data: result.highly_cited },
          { label: "High Impact", data: result.high_impact },
          { label: "Research Articles", data: result.research },
          { label: "Review Articles", data: result.review },
        ];

  const currentTab = tabs[tab];

  return (
    <div
      className="r-layout"
      style={{
        display: "flex",
        gap: 24,
        padding: 24,
        fontFamily: "'DM Sans', sans-serif",
        minHeight: "100%",
      }}
    >
      {/* ── SIDEBAR ── */}
      <aside
        className="r-sidebar"
        style={{
          width: 260,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          position: "sticky",
          top: 24,
          alignSelf: "flex-start",
        }}
      >
        {/* back button */}
        <button
          className="r-back-btn"
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: "#1e3a8a",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 8,
            padding: "7px 14px",
            cursor: "pointer",
            transition: "background 0.15s",
            width: "fit-content",
          }}
        >
          <BackArrow />
          Back to Search
        </button>

        {/* search params */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f8",
            padding: 20,
          }}
          className="r-panel"
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              marginBottom: 14,
            }}
          >
            Search parameters
          </div>
          {[
            { label: "Query", value: query },
            {
              label: "Year range",
              value: `Last ${yearRange} year${yearRange > 1 ? "s" : ""}`,
            },
            { label: "Mode", value: mode === "A" ? "Fast" : "Impact" },
          ].map(({ label, value }) => (
            <div
              key={label}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                padding: "10px 0",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {label}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* results summary */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f8",
            padding: 20,
          }}
          className="r-panel"
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              marginBottom: 14,
            }}
          >
            Results summary
          </div>
          <div
            className="r-stat-grid"
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            {tabs.map((t, i) => (
              <div
                key={i}
                onClick={() => setTab(i)}
                style={{
                  background: tab === i ? "#eff6ff" : "#f8fafc",
                  borderRadius: 10,
                  padding: "10px 12px",
                  textAlign: "center",
                  cursor: "pointer",
                  border:
                    tab === i ? "1px solid #bfdbfe" : "1px solid transparent",
                  transition: "all 0.15s",
                }}
              >
                <div
                  style={{ fontSize: 20, fontWeight: 800, color: "#1e3a8a" }}
                >
                  {t.data?.length || 0}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "#94a3b8",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                    marginTop: 2,
                  }}
                >
                  {t.label.replace(" Articles", "")}
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* heading */}
        <div>
          <h1
            style={{
              fontSize: "clamp(1.4rem,3vw,1.9rem)",
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.5px",
              lineHeight: 1.2,
            }}
          >
            Literature Survey{" "}
            <span
              style={{
                background: "linear-gradient(90deg,#1e3a8a,#3b82f6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Results
            </span>
          </h1>
        </div>

        {/* tabs */}
        <div
          className="r-tabs-wrap"
          style={{
            display: "flex",
            gap: 4,
            background: "#f1f5f9",
            borderRadius: 12,
            padding: 4,
            border: "1px solid #e2e8f8",
            width: "fit-content",
            maxWidth: "100%",
          }}
        >
          {tabs.map((t, i) => (
            <button
              key={i}
              onClick={() => setTab(i)}
              className={tab === i ? "r-tab-active" : ""}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                fontWeight: 600,
                border: "none",
                background: "transparent",
                borderRadius: 8,
                padding: "7px 14px",
                cursor: "pointer",
                color: tab === i ? "#fff" : "#64748b",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
            >
              {t.label}
              <span
                style={{
                  fontSize: 11,
                  background: tab === i ? "rgba(255,255,255,0.25)" : "#dbeafe",
                  color: tab === i ? "#fff" : "#1e3a8a",
                  borderRadius: 100,
                  padding: "2px 8px",
                  fontWeight: 700,
                  marginLeft: 6,
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                {t.data?.length || 0}
              </span>
            </button>
          ))}
        </div>

        {/* paper cards */}
        <div className="r-cards-list" key={tab}>
          {currentTab?.data?.length > 0 ? (
            currentTab.data.map((p, i) => (
              <PaperCard key={p.pmid || i} paper={p} index={i} />
            ))
          ) : (
            <EmptyState label={currentTab?.label} />
          )}
        </div>
      </main>
    </div>
  );
}
